import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  FaUsers, FaCrown, FaCheckCircle, 
  FaRobot, FaBrain, 
  FaMapMarkedAlt, FaFlagCheckered, 
  FaStar, FaEnvelopeOpenText, FaExclamationTriangle 
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // API URL'ini kendi portuna göre ayarla
    axios.get(`${API_URL}/admin/detailed-stats`)
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const calculateTimeAgo = (isoString) => {
    if (!isoString) return "";
    const diff = Math.floor((new Date() - new Date(isoString)) / 1000);
    if (diff < 60) return "Az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  };

  if (loading) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-500 font-sans">Veriler Yükleniyor...</div>;

  const m = data?.metrics || {};

  // --- İSTATİSTİK GRUPLARI ---

  // 1. Kullanıcılar
  const userStats = [
    { label: "Toplam Kullanıcı", value: m.total_users, icon: <FaUsers />, color: "text-blue-400", border: "hover:border-blue-500" },
    { label: "Doğrulanma Oranı", value: m.verified_ratio, icon: <FaCheckCircle />, color: "text-teal-400", border: "hover:border-teal-500" },
    { label: "Premium Üyeler", value: m.premium_count, icon: <FaCrown />, color: "text-yellow-400", border: "hover:border-yellow-500" },
  ];

  // 2. Sistem & İçerik
  const systemStats = [
    { label: "Toplam AI Tahmini", value: m.total_predictions, icon: <FaRobot />, color: "text-purple-400", border: "hover:border-purple-500" },
    { label: "Ort. AI Güveni", value: m.ai_confidence, icon: <FaBrain />, color: "text-pink-400", border: "hover:border-pink-500" },
    { label: "Rota Tamamlama", value: m.completion_rate, icon: <FaFlagCheckered />, color: "text-green-400", border: "hover:border-green-500" },
  ];

  // 3. Geri Bildirim (Yeni Kısım Eklendi)
  const supportStats = [
    { label: "Ortalama Puan", value: m.avg_rating, icon: <FaStar />, color: "text-yellow-500", border: "hover:border-yellow-600" },
    { label: "Bekleyen Mesaj", value: m.unread_messages, icon: <FaEnvelopeOpenText />, color: "text-blue-400", border: "hover:border-blue-500" },
    // Yeni Eklenen Kart:
    { label: "Hatalı Tahmin Bildirimi", value: m.total_reports, icon: <FaExclamationTriangle />, color: "text-orange-500", border: "hover:border-orange-600" },
  ];

  return (
    <div className="flex-1 p-8 bg-gray-900 text-gray-50 min-h-screen font-sans">
      
      {/* Header */}
      <div className="mb-10 border-b border-gray-800 pb-4 flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-extrabold text-white tracking-tight">Mekanname Panel</h1>
          <p className="text-gray-400 mt-2 text-sm">Sistem genel durumu ve kullanıcı aktiviteleri</p>
        </div>
        <div className="text-right hidden sm:block">
            <span className="bg-gray-800 text-gray-400 px-3 py-1 rounded text-xs border border-gray-700">v3.0.1 Stable</span>
        </div>
      </div>

      {/* 1. SATIR: Kullanıcı Verileri */}
      <h3 className="text-gray-500 uppercase text-xs font-bold mb-4 tracking-wider">Kullanıcı İstatistikleri</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {userStats.map((stat, idx) => (
          <StatCard key={idx} stat={stat} />
        ))}
      </div>

      {/* 2. SATIR: Sistem ve İçerik */}
      <h3 className="text-gray-500 uppercase text-xs font-bold mb-4 tracking-wider">Sistem & İçerik Sağlığı</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {systemStats.map((stat, idx) => (
          <StatCard key={idx} stat={stat} />
        ))}
      </div>

      {/* 3. SATIR: Grid Yapısı (Sol: Geri Bildirim, Sağ: Tablo) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Sol Kolon: Geri Bildirim Kartları */}
        <div className="flex flex-col gap-6">
           <h3 className="text-gray-500 uppercase text-xs font-bold tracking-wider">Geri Bildirim</h3>
           {supportStats.map((stat, idx) => (
              <StatCard key={idx} stat={stat} />
            ))}
        </div>

        {/* Sağ Kolon: Son Aktiviteler Tablosu */}
        <div className="lg:col-span-2">
           <h3 className="text-gray-500 uppercase text-xs font-bold mb-4 tracking-wider">Son AI Sorguları</h3>
           <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 overflow-hidden">
             <div className="overflow-x-auto">
               <table className="w-full text-left border-collapse">
                 <thead>
                   <tr className="bg-gray-800/50 text-gray-400 text-xs uppercase border-b border-gray-700">
                     <th className="p-4 font-semibold">Kullanıcı</th>
                     <th className="p-4 font-semibold">Tahmin Edilen Yer</th>
                     <th className="p-4 font-semibold text-center">AI Güveni</th>
                     <th className="p-4 font-semibold text-right">Zaman</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-700 text-sm">
                   {data?.recent_activity?.map((item, idx) => (
                     <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                       <td className="p-4">
                         <div className="font-medium text-white">{item.user}</div>
                         <div className="text-gray-500 text-xs">{item.email}</div>
                       </td>
                       <td className="p-4 text-gray-300">{item.location || "Tanımsız"}</td>
                       <td className="p-4 text-center">
                         <span className={`px-2 py-1 rounded text-xs font-bold ${
                           item.confidence > 80 ? 'bg-green-900 text-green-300' : 
                           item.confidence > 50 ? 'bg-yellow-900 text-yellow-300' : 'bg-red-900 text-red-300'
                         }`}>
                           %{item.confidence}
                         </span>
                       </td>
                       <td className="p-4 text-right text-gray-400 font-mono text-xs">
                         {calculateTimeAgo(item.timestamp)}
                       </td>
                     </tr>
                   ))}
                   {(!data?.recent_activity || data.recent_activity.length === 0) && (
                      <tr><td colSpan="4" className="p-4 text-center text-gray-500">Henüz veri yok.</td></tr>
                   )}
                 </tbody>
               </table>
             </div>
           </div>
        </div>

      </div>
    </div>
  );
}

// Kart Bileşeni
function StatCard({ stat }) {
  return (
    <div className={`bg-gray-800 p-5 rounded-2xl shadow-lg border border-gray-700 ${stat.border} transition-all duration-300 flex items-center justify-between group`}>
      <div>
        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{stat.label}</p>
        <p className="text-3xl font-bold mt-1 text-white group-hover:scale-105 transition-transform origin-left">{stat.value}</p>
      </div>
      <div className={`text-3xl ${stat.color} opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-transform`}>
        {stat.icon}
      </div>
    </div>
  );
}